import React, { useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'

interface MotherThinkingProps {
  currentMessage?: string
  isThinking: boolean
  size?: 'small' | 'medium' | 'large'
  sx?: any
}

let MotherThinking = React.forwardRef<HTMLDivElement, MotherThinkingProps>(
  (
    { currentMessage, isThinking, size = 'medium', sx }: MotherThinkingProps,
    ref: React.Ref<HTMLDivElement>,
  ): React.ReactElement | null => {
    if (!isThinking) {
      return null
    }

    let sizeMap = {
      small: 80,
      medium: 120,
      large: 160,
    }

    let messageSizeMap = {
      small: 'body2' as const,
      medium: 'body1' as const,
      large: 'subtitle1' as const,
    }

    let svgRef = useRef<SVGSVGElement>(null)

    useEffect(() => {
      if (!svgRef.current) return

      let paths = svgRef.current.querySelectorAll('path')
      if (paths.length === 0) return

      // Define timing
      let drawDuration = 1.2
      let pauseBetweenPaths = 0.1
      let totalCycleDuration =
        drawDuration * paths.length + pauseBetweenPaths * (paths.length - 1)

      // Calculate actual path lengths and create keyframes dynamically
      let keyframes = ''

      Array.from(paths).forEach((path, index) => {
        let length = path.getTotalLength()

        // Set stroke properties
        // eslint-disable-next-line no-param-reassign,fp/no-mutation
        path.style.strokeDasharray = String(length)
        // eslint-disable-next-line no-param-reassign,fp/no-mutation
        path.style.strokeDashoffset = String(length)

        // Calculate percentage points in the animation cycle
        let startPercent =
          ((index * (drawDuration + pauseBetweenPaths)) / totalCycleDuration) *
          100
        let endPercent =
          ((index * (drawDuration + pauseBetweenPaths) + drawDuration) /
            totalCycleDuration) *
          100

        // Build the keyframe for this path
        let pathKeyframe = `
          @keyframes draw-path-${index} {
            0% { stroke-dashoffset: ${length}; }
            ${startPercent}% { stroke-dashoffset: ${length}; }
            ${endPercent}% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: 0; }
          }
        `
        // eslint-disable-next-line fp/no-mutation
        keyframes += pathKeyframe

        // Apply the animation to the path
        // eslint-disable-next-line no-param-reassign,fp/no-mutation
        path.style.animation = `draw-path-${index} ${totalCycleDuration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite`
      })

      // Inject the keyframes into a style element

      let styleEl = document.createElement('style')
      // eslint-disable-next-line fp/no-mutation
      styleEl.textContent = keyframes

      document.head.appendChild(styleEl)

      // eslint-disable-next-line consistent-return
      return () => {
        document.head.removeChild(styleEl)
      }
    }, [])

    return (
      <>
        <style>
          {`
            /* Container holds both SVGs stacked */
            .mother-icon-wrapper {
              position: relative;
              display: inline-block;
              width: 100%;
              height: 100%;
            }

            /* Base static icon - fully visible and filled */
            .mother-icon-base {
              position: relative;
              display: block;
              width: 100%;
              height: 100%;
              stroke: #6694e9;
              stroke-opacity: 0.8;
              fill: #6694e9;
              fill-opacity: 0.3;
              stroke-width: 1.5px;
              stroke-linecap: round;
              stroke-linejoin: round;
            }

            /* Animated overlay - draws outline on top */
            .mother-icon-animated {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              fill: none;
              stroke-width: 1.5px;
              stroke-linecap: round;
              stroke-linejoin: round;
              pointer-events: none;
            }

            /* Animated paths with glow - animation applied via JS */
            .mother-icon-animated path {
              stroke: #f9ea4f;
              stroke-opacity: 0.5;
              filter: drop-shadow(0 0 6px rgba(249, 234, 79, 0.6))
                drop-shadow(0 0 12px rgba(249, 234, 79, 0.4))
                drop-shadow(0 0 20px rgba(249, 234, 79, 0.3));
            }

            /* Letter-by-letter fade-in animation */
            @keyframes letter-fade-in {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            .mother-message-letter {
              animation: letter-fade-in 0.1s ease-in forwards;
            }

            /* Accessibility: Respect reduced motion preference */
            @media (prefers-reduced-motion: reduce) {
              .mother-icon-animated path {
                animation: none !important;
                stroke-dashoffset: 0 !important;
                filter: none;
              }
              .mother-message-letter {
                animation: none !important;
              }
            }
          `}
        </style>

        <Box
          ref={ref}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 3,
            ...sx,
          }}
        >
          <div
            className="mother-icon-wrapper"
            style={{
              width: sizeMap[size],
              height: sizeMap[size],
            }}
          >
            {/* Static base icon with fill */}
            <svg
              className="mother-icon-base"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 161.84 150.83"
              preserveAspectRatio="xMidYMid meet"
            >
              <path d="M72.25,61.34c-.06-.05-.13-.09-.19-.13L20.91,3.08C12.31-3.4,0,2.73,0,13.5v124.28c0,7.21,5.84,13.05,13.05,13.05h7.09l61.54-78.74-9.42-10.74Z" />
              <path d="M117.6,127.7h-28.86c-.92,0-1.42-1.07-.81-1.75l20.1-22.44c.35-.4.36-.99,0-1.39l-14.49-16.52-50.79,65.22h67.03l8.81-21.68c.28-.69-.24-1.45-1-1.45Z" />
              <path d="M141.07,2.55l-50.68,55.73,38.22,41.91c.37.4.36,1.01,0,1.41l-9.92,10.71c-.63.68-.13,1.77.81,1.76l29.01-.3c.94,0,1.44,1.09.8,1.77l-32.96,35.28h32.45c7.21,0,13.05-5.84,13.05-13.05V13.07c0-10.69-12.15-16.85-20.77-10.52Z" />
            </svg>

            {/* Animated glow outline overlay */}
            <svg
              ref={svgRef}
              className="mother-icon-animated"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 161.84 150.83"
              preserveAspectRatio="xMidYMid meet"
            >
              <path d="M72.25,61.34c-.06-.05-.13-.09-.19-.13L20.91,3.08C12.31-3.4,0,2.73,0,13.5v124.28c0,7.21,5.84,13.05,13.05,13.05h7.09l61.54-78.74-9.42-10.74Z" />
              <path d="M117.6,127.7h-28.86c-.92,0-1.42-1.07-.81-1.75l20.1-22.44c.35-.4.36-.99,0-1.39l-14.49-16.52-50.79,65.22h67.03l8.81-21.68c.28-.69-.24-1.45-1-1.45Z" />
              <path d="M141.07,2.55l-50.68,55.73,38.22,41.91c.37.4.36,1.01,0,1.41l-9.92,10.71c-.63.68-.13,1.77.81,1.76l29.01-.3c.94,0,1.44,1.09.8,1.77l-32.96,35.28h32.45c7.21,0,13.05-5.84,13.05-13.05V13.07c0-10.69-12.15-16.85-20.77-10.52Z" />
            </svg>
          </div>

          {currentMessage && (
            <Typography
              variant={messageSizeMap[size]}
              color="text.secondary"
              sx={{ textAlign: 'center', maxWidth: 300, animation: 'none' }}
              key={`message-${currentMessage}`}
            >
              {currentMessage.split('').map((char, index) => (
                <span
                  key={index}
                  className="mother-message-letter"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {char}
                </span>
              ))}
            </Typography>
          )}
          {!currentMessage && (
            <Typography
              variant={messageSizeMap[size]}
              color="text.secondary"
              sx={{ textAlign: 'center', animation: 'none' }}
            >
              {'MOTHER is thinking...'.split('').map((char, index) => (
                <span
                  key={`thinking-${index}`}
                  className="mother-message-letter"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {char}
                </span>
              ))}
            </Typography>
          )}
        </Box>
      </>
    )
  },
)

export default MotherThinking
